import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiRequest } from './apiClient';
import './auth.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const pending = searchParams.get('pending') === '1';
  const pendingEmail = searchParams.get('email') || '';
  // On-screen link fallback when SendGrid is not configured. Carried via the
  // ?link= query param from SignupPage (and rendered after a "didn't get
  // it?" resend below).
  const initialLink = searchParams.get('link') || '';

  const [status, setStatus] = useState(token ? 'verifying' : (pending ? 'pending' : 'idle'));
  const [message, setMessage] = useState(null);
  const [resendInfo, setResendInfo] = useState(null);
  const [resendEmail, setResendEmail] = useState(pendingEmail);
  const [resending, setResending] = useState(false);
  const [onScreenLink, setOnScreenLink] = useState(initialLink);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { ok, data } = await apiRequest('GET', `${BACKEND_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified. You can now log in.');
        } else {
          setStatus('error');
          setMessage(typeof data.detail === 'string' ? data.detail : 'Verification failed.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('Network error: ' + err.message);
      }
    })();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendInfo(null);
    setResending(true);
    try {
      const { data } = await apiRequest('POST', `${BACKEND_URL}/api/auth/resend-verification`, {
        body: { email: resendEmail },
      });
      setResendInfo(data.message || 'If your email is unverified, a new verification link has been sent.');
      if (data.verification_link) setOnScreenLink(data.verification_link);
    } catch (err) {
      setResendInfo('Network error: ' + err.message);
    } finally {
      setResending(false);
    }
  };

  // Reusable on-screen link panel used in both the "pending" and "error"
  // states. Renders only when SendGrid has not yet been configured and the
  // backend returns the link directly.
  const onScreenLinkPanel = onScreenLink ? (
    <div
      data-testid="verify-onscreen-link"
      style={{
        background: '#fef9c3', border: '1px solid #fde047', color: '#713f12',
        padding: '0.75rem 0.85rem', borderRadius: 6, fontSize: '0.85rem',
        marginTop: '0.85rem', wordBreak: 'break-all',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Email delivery is not yet configured. Use this verification link directly:
      </div>
      <a href={onScreenLink} style={{ color: '#0073EA', fontWeight: 500 }}>{onScreenLink}</a>
    </div>
  ) : null;

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="verify-card">
        <div className="auth-brand"><span className="auth-brand-dot"></span> Estatel AI</div>

        {status === 'pending' && (
          <>
            <h1 className="auth-title">Check your inbox</h1>
            <p className="auth-subtitle">
              We've sent a verification link to <strong>{pendingEmail}</strong>. Click the link to activate your account. The link expires in 24 hours.
            </p>
            {onScreenLinkPanel}
            <form onSubmit={handleResend}>
              <div className="auth-field">
                <label htmlFor="resend-email">Didn't receive it?</label>
                <input id="resend-email" type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} required data-testid="verify-resend-email" />
              </div>
              {resendInfo && <div className="auth-info" data-testid="verify-resend-info">{resendInfo}</div>}
              <button
                type="submit"
                disabled={resending}
                style={{
                  width: '100%', background: resending ? '#94a3b8' : '#0073EA', color: '#fff',
                  border: 'none', borderRadius: 6, padding: '0.7rem', fontSize: '0.95rem',
                  fontWeight: 500, cursor: resending ? 'not-allowed' : 'pointer',
                }}
                data-testid="verify-resend-submit"
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
            </form>
            <div className="auth-footer">
              <Link to="/login">Back to sign in</Link>
            </div>
          </>
        )}

        {status === 'verifying' && (
          <>
            <h1 className="auth-title">Verifying your email…</h1>
            <p className="auth-subtitle">Hang tight — this only takes a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="auth-title">Email verified</h1>
            <div className="auth-success" data-testid="verify-success">{message}</div>
            <Link
              to="/login"
              style={{
                display: 'block', textAlign: 'center', background: '#0073EA', color: '#fff',
                border: 'none', borderRadius: 6, padding: '0.7rem', fontSize: '0.95rem',
                fontWeight: 500, textDecoration: 'none', marginTop: '0.5rem',
              }}
              data-testid="verify-go-login"
            >
              Go to sign in
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="auth-title">Verification failed</h1>
            <div className="auth-error" data-testid="verify-error">{message}</div>
            <form onSubmit={handleResend}>
              <div className="auth-field">
                <label htmlFor="resend-email-err">Resend verification email</label>
                <input id="resend-email-err" type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} required placeholder="your.email@example.com" data-testid="verify-resend-email" />
              </div>
              {resendInfo && <div className="auth-info">{resendInfo}</div>}
              <button
                type="submit"
                disabled={resending}
                style={{
                  width: '100%', background: resending ? '#94a3b8' : '#0073EA', color: '#fff',
                  border: 'none', borderRadius: 6, padding: '0.7rem', fontSize: '0.95rem',
                  fontWeight: 500, cursor: resending ? 'not-allowed' : 'pointer',
                }}
                data-testid="verify-resend-submit"
              >
                {resending ? 'Sending…' : 'Send a new verification link'}
              </button>
            </form>
            <div className="auth-footer">
              <Link to="/login">Back to sign in</Link>
            </div>
          </>
        )}

        {status === 'idle' && (
          <>
            <h1 className="auth-title">Verify your email</h1>
            <p className="auth-subtitle">This page expects a verification token in the URL.</p>
            <div className="auth-footer"><Link to="/login">Back to sign in</Link></div>
          </>
        )}
      </div>
    </div>
  );
}
